import React from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline, Container, Typography, AppBar, Toolbar, Menu, MenuItem, IconButton, Box, Avatar, Drawer, List, ListItemIcon, ListItemText, ListItemButton, Divider, Badge, Snackbar, Alert } from '@mui/material';
import EventList from './components/EventList';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { UserProvider, useUser } from './contexts/UserContext';
import Login from './components/Login';
import Signup from './components/Signup';
import CreateSite from './components/CreateSite';
import ShareGroupLink from './components/ShareGroupLink';
import SiteManagement from './components/SiteManagement';
import GlobalAdminDashboard from './components/GlobalAdminDashboard';
// Removed ParticipantHistory
import { 
  AccountCircleOutlined as AccountIcon, 
  NotificationsOutlined as NotificationsIcon, 
  MenuOutlined as MenuIcon,
  HomeOutlined as HomeIcon,
  SettingsOutlined as SettingsIcon,
  DashboardOutlined as DashboardIcon,
  AdminPanelSettingsOutlined as AdminPanelIcon,
  Share as ShareIcon
} from '@mui/icons-material';
// Removed ParticipantHome
import UserHome from './components/UserHome';
import GuestHome from './components/GuestHome';
import SettingsPage from './components/SettingsPage';
import NotificationCenter from './components/NotificationCenter';

const theme = createTheme({
  palette: {
    primary: {
      main: '#2484cc',
    },
    secondary: {
      main: '#b0bec5',
    },
  },
});

function App() {
  return (
    <AuthProvider>
      <UserProvider>
        <AppContent />
      </UserProvider>
    </AuthProvider>
  );
}

function AppContent() {
  const { isAuthenticated } = useAuth();
  const { isParticipant, user } = useUser();
  
  // Combine authentication states - user is authenticated if either context shows them as authenticated
  const isUserAuthenticated = isAuthenticated || !!user;
  
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <TopAppBar />
        <AppRoutes isAuthenticated={isUserAuthenticated} participantAuthenticated={isParticipant} />
        {/* <AppFooter isAuthenticated={isUserAuthenticated} participantAuthenticated={isParticipant} /> */}
      </Router>
    </ThemeProvider>
  );
}

function TopAppBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { user, isAdmin, userSites, joinedSites, refreshUserSites, displayInitial, unreadAnnouncementsCount } = useUser();
  const isUserAuthenticated = isAuthenticated || !!user;
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const open = Boolean(anchorEl);
  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const toggleDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' ||
        (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }
    setDrawerOpen(open);
    // Always refresh user sites when opening drawer to get latest data
    if (open && isAdmin) {
      refreshUserSites();
    }
  };

  // Lock body scroll when drawer is open
  React.useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [drawerOpen]);
  const isSiteManagement = location.pathname.includes('/site-management');
  
  const handleNavigation = (path: string) => {
    navigate(path);
    setDrawerOpen(false);
  };
  
  if (isSiteManagement) return null;
  return (
    <>
      {/* Slide-out Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        disableScrollLock={false}
        PaperProps={{
          sx: {
            width: 280,
            bgcolor: 'background.paper',
            paddingBottom: 'env(safe-area-inset-bottom)',
          }
        }}
      >
        <Box
          sx={{ width: 280 }}
          role="presentation"
        >
          {/* Drawer Header */}
          <Box sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <img src="./logo.png" alt="MatchPoint Logo" style={{ width: 32, height: 32 }} />
              <Typography variant="h5" sx={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400, letterSpacing: '-0.02em' }}>
                MatchPoint
              </Typography>
            </Box>
            <IconButton onClick={toggleDrawer(false)} size="small">
              <MenuIcon />
            </IconButton>
          </Box>

          <Divider />

          {/* Main Navigation */}
          <List sx={{ px: 1, py: 2 }}>
            <ListItemButton onClick={() => handleNavigation('/')} sx={{ borderRadius: 1, mb: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <HomeIcon />
              </ListItemIcon>
              <ListItemText primary="首頁" />
            </ListItemButton>

            {isUserAuthenticated && (
              <>
                {/* <ListItemButton onClick={() => handleNavigation('/settings')} sx={{ borderRadius: 1, mb: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <ProfileIcon />
                  </ListItemIcon>
                  <ListItemText primary="個人資料" />
                </ListItemButton> */}

                <ListItemButton onClick={() => handleNavigation('/notifications')} sx={{ borderRadius: 1, mb: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <NotificationsIcon />
                  </ListItemIcon>
                  <ListItemText primary="通知" />
                </ListItemButton>

                <ListItemButton onClick={() => handleNavigation('/settings')} sx={{ borderRadius: 1, mb: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <SettingsIcon />
                  </ListItemIcon>
                  <ListItemText primary="設定" />
                </ListItemButton>
              </>
            )}
          </List>

          {/* Joined Sites Section */}
          {isUserAuthenticated && joinedSites.length > 0 && (
            <>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ px: 2, py: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  已加入群組
                </Typography>
              </Box>
              <List sx={{ px: 1, py: 0 }}>
                {joinedSites.map((site) => (
                  <ListItemButton 
                    key={site.siteId}
                    onClick={() => handleNavigation(`/${site.siteRoute}`)} 
                    sx={{ borderRadius: 1, mb: 0.5, pl: 2 }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <HomeIcon />
                    </ListItemIcon>
                    <ListItemText 
                      primary={site.siteName} 
                    />
                  </ListItemButton>
                ))}
              </List>
            </>
          )}

          {/* Managing Sites Section */}
          {isAdmin && (
            <>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  管理群組
                </Typography>
              </Box>
              <List sx={{ px: 1, py: 0 }}>
                {userSites.map((site) => (
                  <Box key={site._id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <ListItemButton 
                      onClick={() => handleNavigation(`/${site.route}`)} 
                      sx={{ borderRadius: 1, pl: 2, flex: 1 }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <AdminPanelIcon />
                      </ListItemIcon>
                      <ListItemText 
                        primary={site.name} 
                        secondary={`/${site.route}`}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItemButton>
                    <IconButton
                      size="small"
                      onClick={async (e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        
                        const groupUrl = `https://matchpoint.hk/${site.route}`;
                        try {
                          await navigator.clipboard.writeText(groupUrl);
                          setSnackbarOpen(true);
                        } catch (err) {
                          console.error('Failed to copy:', err);
                        }
                      }}
                      sx={{ mr: 1 }}
                      title="複製連結"
                    >
                      <ShareIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </List>
            </>
          )}
          
          {/* Create New Site Button for authenticated users */}
          {isUserAuthenticated && (
            <>
              {!isAdmin && <Divider sx={{ my: 1 }} />}
              <List sx={{ px: 1, py: 1 }}>
                <ListItemButton onClick={() => handleNavigation('/create-site')} sx={{ borderRadius: 1, bgcolor: 'primary.light', '&:hover': { bgcolor: 'primary.main' } }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <DashboardIcon sx={{ color: 'white' }} />
                  </ListItemIcon>
                  <ListItemText primary="創建新群組" sx={{ color: 'white' }} />
                </ListItemButton>
              </List>
            </>
          )}

          {/* Global Admin Section */}
          {user?.roles?.includes('global_admin') && (
            <>
              <Divider sx={{ my: 1 }} />
              <List sx={{ px: 1, py: 1 }}>
                <ListItemButton onClick={() => handleNavigation('/global-admin')} sx={{ borderRadius: 1 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <DashboardIcon />
                  </ListItemIcon>
                  <ListItemText primary="全域管理" />
                </ListItemButton>
              </List>
            </>
          )}

          {/* Guest Actions */}
          {!isUserAuthenticated && (
            <>
              <Divider sx={{ my: 1 }} />
              <List sx={{ px: 1, py: 1 }}>
                <ListItemButton onClick={() => handleNavigation('/login')} sx={{ borderRadius: 1, mb: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <AccountIcon />
                  </ListItemIcon>
                  <ListItemText primary="登入" />
                </ListItemButton>
                <ListItemButton onClick={() => handleNavigation('/signup')} sx={{ borderRadius: 1 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <AccountIcon />
                  </ListItemIcon>
                  <ListItemText primary="建立您的群組" />
                </ListItemButton>
              </List>
            </>
          )}
        </Box>
      </Drawer>

      <AppBar 
        position="fixed" 
        sx={{ 
          top: 0, 
          left: 0,
          right: 0,
          bgcolor: 'background.paper', 
          color: 'text.primary', 
          boxShadow: 0, 
          borderBottom: 1, 
          borderColor: 'divider', 
          zIndex: (theme) => theme.zIndex.appBar, 
          paddingTop: 'env(safe-area-inset-top)',
          /* Fix for iOS Safari to prevent AppBar from moving */
          WebkitTransform: 'translate3d(0, 0, 0)',
          transform: 'translate3d(0, 0, 0)',
        }}
      >
        <Toolbar sx={{ px: { xs: 2, sm: 3 }, minHeight: { xs: 56, sm: 64 } }}>
          {/* Hamburger Menu */}
          <IconButton
            edge="start"
            color="default"
            aria-label="menu"
            onClick={toggleDrawer(true)}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>

        {/* Logo/Brand */}
        <Box onClick={() => navigate('/')} sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <img src="./logo.png" alt="MatchPoint Logo" style={{ width: 40, height: 40 }} />
          <Typography sx={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400, fontSize: { xs: '1.5rem', sm: '2rem' }, letterSpacing: '-0.02em' }} variant="h6">
            MatchPoint
          </Typography>
        </Box>

        {/* Spacer */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Right side icons */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>

          {isUserAuthenticated ? (
            <>
              {/* Notifications with Badge */}
              <IconButton color="default" onClick={() => navigate('/notifications')}>
                <Badge badgeContent={unreadAnnouncementsCount} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
              {/* User Profile with Initial */}
              <IconButton
                onClick={() => navigate('/settings')}
                sx={{
                  p: 0,
                  '&:hover': {
                    bgcolor: 'transparent',
                  },
                }}
              >
                <Avatar 
                  src={user?.avatarType === 'image' && user?.avatarUrl ? user.avatarUrl : undefined}
                  sx={{ 
                    width: 40, 
                    height: 40, 
                    bgcolor: user?.avatarType === 'image' ? '#ffffff' : (user?.avatarColor || 'primary.main'),
                    border: '2px solid',
                    borderColor: user?.avatarType === 'image' ? 'divider' : 'transparent',
                    fontSize: '1rem',
                    fontWeight: 600,
                    '&:hover': {
                      bgcolor: user?.avatarType === 'image' ? '#ffffff' : (user?.avatarColor ? `${user.avatarColor}dd` : 'primary.dark'),
                      borderColor: user?.avatarType === 'image' ? 'primary.main' : 'transparent',
                    },
                  }}
                >
                  {user?.avatarType === 'initial' && user?.avatarInitial 
                    ? user.avatarInitial
                    : displayInitial
                  }
                </Avatar>
              </IconButton>
            </>
          ) : (
            <>
              <IconButton
                aria-label="account"
                onClick={handleMenuOpen}
                sx={{
                  p: 0,
                  '&:hover': {
                    bgcolor: 'transparent',
                  },
                }}
              >
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: 'primary.main',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    },
                  }}
                >
                  <AccountIcon />
                </Avatar>
              </IconButton>
              <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose} PaperProps={{ sx: { minWidth: 160 } }}>
                <MenuItem onClick={() => { handleMenuClose(); navigate('/login'); }}>登入</MenuItem>
                <MenuItem onClick={() => { handleMenuClose(); navigate('/signup'); }}>建立您的群組</MenuItem>
              </Menu>
            </>
          )}
        </Box>
      </Toolbar>
    </AppBar>
    
    {/* Success message for copy link */}
    <Snackbar 
      open={snackbarOpen} 
      autoHideDuration={2000} 
      onClose={() => setSnackbarOpen(false)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert onClose={() => setSnackbarOpen(false)} severity="success" sx={{ width: '100%' }}>
        連結已複製到剪貼簿
      </Alert>
    </Snackbar>
    </>
  );
}

function AppRoutes({ isAuthenticated, participantAuthenticated }: { isAuthenticated: boolean; participantAuthenticated: boolean }) {
  const location = useLocation();
  const isSiteManagement = location.pathname.includes('/site-management');
  
  if (isSiteManagement) {
    return (
      <Routes>
        <Route path="/:route/site-management" element={<SiteManagement />} />
      </Routes>
    );
  }
  
  // Removed participant history route
  
  return (
    <Container maxWidth={false} disableGutters sx={{ pt: { xs: 'calc(57px + env(safe-area-inset-top))', sm: 'calc(57px + env(safe-area-inset-top))' }, width: '100%' }}>
      <Routes>
        <Route path="/" element={(isAuthenticated || participantAuthenticated) ? <UserHome /> : <GuestHome />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/signup/*" element={<Signup />} />
        <Route path="/create-site" element={<CreateSite />} />
        <Route path="/share-group" element={<ShareGroupLink />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/notifications" element={<NotificationCenter />} />
        <Route path="/global-admin" element={<GlobalAdminDashboard />} />
        {/* Removed participant history route */}
        <Route path="/:route/event/:eventId" element={<EventList />} />
        <Route path="/:route" element={<EventList />} />
      </Routes>
    </Container>
  );
}

export default App;